import type { FC, ReactNode } from "react";
import React, { useState } from "react";
import type { PopoverActions} from "@mui/material";
import { Button, Popover, useMediaQuery } from "@mui/material";
import { Tune } from "@mui/icons-material";
import { useTheme, withStyles } from "@mui/styles";
import Edit from "../../icons/edit";
import { popoverStyles } from "../../styles";

type Props = {
  children?: ReactNode;
  disabled?: boolean;
};
const StyledPopover = withStyles(popoverStyles)(Popover);

const EditReportButton: FC<Props> = ({ children, disabled }) => {
  const theme = useTheme();
  const isMob: boolean = useMediaQuery(theme.breakpoints.down("sm"));
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const popoverActions = React.useRef<PopoverActions | null>(null);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const childrenWithProps = React.Children.map(children, (child) => {
    type PropsToInject = {
      onClose: () => void;
      open: boolean;
      onUpdatePosition: () => void;
    };
    if (React.isValidElement<PropsToInject>(child)) {
      return React.cloneElement(child, {
        onClose: handleClose,
        open,
        onUpdatePosition: () => popoverActions.current?.updatePosition(),
      });
    }

    return child;
  });
  return (
    <>
      <Button
        sx={{
          width: "100%",
          padding: {
            sm: "7px 16px",
            lg: "10px 16px",
          },
          height: {
            sm: "40px",
            lg: "50px",
          },
          fontSize: {
            xs: "14px",
            lg: "16px",
          },
          lineHeight: {
            sm: "20px",
            lg: "16px",
          },
        }}
        variant="gradient"
        onClick={handleClick}
        disabled={disabled}
        startIcon={isMob ? <Tune /> : <Edit />}
        data-testid="edit-report-btn"
      >
        Edit Report
      </Button>
      <StyledPopover
        action={popoverActions}
        marginThreshold={isMob ? 0 : 20}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {childrenWithProps}
      </StyledPopover>
    </>
  );
};

export default EditReportButton;
